import React, { useMemo } from 'react';
import useSWR from 'swr';

import { Card } from '@nearst/ui';
import { getFeedQualityStats } from '@services/stock';
import { errorsFor, inStockFor, notShowingFor } from '@services/feedQualityCounts';
import { useShop } from '@services/ShopProvider';
import useChannelInfo from '../../../pages/channels/TemplatePages/useChannelInfo';
import { format } from 'date-fns';
import HorizontalBar from '@components/Graphs/HorizontalBar';

import styles from './FeedQuality.module.scss';

/**
 * Our own numbers: every in-stock row is either active or errored, and the
 * inactive rows complete the catalogue, so the bar is the whole feed.
 */
const ownFeedSegments = (data) => ({
	total: data.totalItems,
	values: [
		{
			title: 'active products',
			description: 'In-stock items with no issues',
			count: data.activeItems,
			style: styles.activeItemsSegment
		},
		{
			title: 'products with errors',
			description: 'In-stock items not visible due to issues',
			count: errorsFor(data),
			style: styles.errorItemsSegment
		},
		{
			title: 'inactive products',
			description: 'Out-of-stock or hidden items',
			count: data.inactiveItems
		}
	]
});

/**
 * Google's numbers: active and disapproved are what Merchant Centre reports,
 * which says nothing about our out-of-stock rows, so the bar is scoped to
 * in-stock items and the remainder is what Google is not listing yet. Mixing
 * the two sources in one total is what collapsed this bar to "<1% active" on
 * every Google page (NEA-6601).
 */
const merchantCentreSegments = (data) => ({
	total: inStockFor(data),
	values: [
		{
			title: 'active products',
			description: 'In-stock items Google is showing',
			count: data.activeItems,
			style: styles.activeItemsSegment
		},
		{
			title: 'products with errors',
			description: 'In-stock items Google has disapproved',
			count: errorsFor(data),
			style: styles.errorItemsSegment
		},
		{
			title: 'products not yet on Google',
			description: 'In-stock items Google is not listing yet',
			count: notShowingFor(data)
		}
	]
});

export const FeedQualityBar = ({ data }) => {
	const segments = useMemo(() => {
		const { total, values } = data.source === 'google-merchant-centre' ? merchantCentreSegments(data) : ownFeedSegments(data);
		for (const segment of values) {
			segment.percentage = total ? Math.min(100, Math.round((segment.count / total) * 100)) : 0;
		}
		return values;
	}, [data]);

	return <HorizontalBar segments={segments} />;
};

const LastFullUpload = () => {
	const { shopChannelConfig } = useChannelInfo();
	const lastFullUpload = shopChannelConfig?.config?.lastFullUpload;
	if (!lastFullUpload) return null;

	const formattedDate = format(new Date(lastFullUpload), 'dd MMM, HH:mm');

	return <div className={styles.lastFullUpload}>Last full upload: {formattedDate}</div>;
};

/**
 * Google reports product statistics per Merchant Centre account rather than per
 * store, so a chain whose locations share one account sees the whole catalogue
 * here, not this shop's shelf. Say so rather than letting it read as
 * store-specific (NEA-6285).
 */
const MerchantScopeNote = ({ merchantScope }) => {
	if (!merchantScope?.shared) return null;

	return (
		<p className={styles.merchantScopeNote}>
			Figures come from your Merchant Centre account, which covers all {merchantScope.locations.toLocaleString()} of your locations —
			not this store alone.
		</p>
	);
};

const FeedQuality = ({ channelId = 'nearst' }) => {
	const { shop } = useShop();
	const key = channelId ? `${shop.id}-${channelId}-feed-quality-stats` : `${shop.id}-feed-quality-stats`;

	const { data, isLoading } = useSWR(key, () => getFeedQualityStats(shop.id, channelId));

	if (!data && !isLoading) return null;

	return (
		<Card title="Feed quality report">
			{isLoading ? (
				<HorizontalBar.Loading />
			) : (
				<>
					<FeedQualityBar data={data} />
					<MerchantScopeNote merchantScope={data.merchantScope} />
					<LastFullUpload />
				</>
			)}
		</Card>
	);
};

export default FeedQuality;
